import axios from "axios";
import * as CryptoJS from "crypto-js";
import { getItem } from "../common/localStorage";
import {
  getBearerToken,
  GUEST_LOGIN_RESPONSE_KEY,
  GUEST_TOKEN_KEY,
  MEMBER_TOKEN_KEY,
} from "../common/authSession";
import { message } from "antd";

export const API_URL = process.env.REACT_APP_API_URL_REFACTOR;

let navigateRef;
export function registerHttpNavigate(navigate) {
  navigateRef = navigate;
}

/** Guest enrollment step mismatch (417): set from `GuestEnrollmentStepMismatchHost`. */
let guestStepMismatchHandler;
export function registerGuestStepMismatchHandler(handler) {
  guestStepMismatchHandler = handler;
}

function notifyGuestStepMismatch(payload) {
  try {
    guestStepMismatchHandler?.(payload);
  } catch {
    /* non-fatal */
  }
}

function rejectGuestStepMismatch(parsed) {
  notifyGuestStepMismatch(parsed);
  return {
    statusCode: parsed?.statusCode ?? 417,
    message: parsed?.message,
    data: parsed?.data,
    error: parsed?.error,
  };
}

const goHomeSpa = () =>
  navigateRef
    ? navigateRef("/", { replace: true })
    : window.history.pushState(null, "", "/");

export function createPayloadKeys(encryptionKey) {
  let mdString = CryptoJS.MD5(encryptionKey).toString();
  let key = CryptoJS.enc.Utf8.parse(mdString.substring(0, 16));
  let iv = CryptoJS.enc.Utf8.parse(mdString.substring(16));
  return { key, iv };
}

// Function to encrypt payload
export const encryptPayload = (textToEncrypt, keyData) => {
  let encrypted = CryptoJS.AES.encrypt(textToEncrypt, keyData.key, {
    iv: keyData.iv,
    mode: CryptoJS.mode.CBC,
    padding: CryptoJS.pad.Pkcs7,
  });
  return encrypted.toString();
};

// Function to decrypt payload
export const decryptPayload = (encryptedText, keyData) => {
  let decrypted = CryptoJS.AES.decrypt(encryptedText, keyData.key, {
    iv: keyData.iv,
    mode: CryptoJS.mode.CBC,
    padding: CryptoJS.pad.Pkcs7,
  });
  return decrypted.toString(CryptoJS.enc.Utf8);
};

// HTTP client class
export default class Http {
  static post(url, body) {
    return new Promise((resolve, reject) => {
      // Generate encryption keys
      const { key, iv } = createPayloadKeys(
        process.env.REACT_APP_ENCRYPTION_KEY,
      );

      // Encrypt the payload
      const encryptedBody = encryptPayload(JSON.stringify(body ?? {}), {
        key,
        iv,
      });

      // Make API request
      axios
        .post(
          API_URL + "/" + url,
          { body: encryptedBody },
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: getBearerToken(),
              brandId: 2,
              lang: getItem("language"),
            },
          },
        )
        .then((response) => {
          // Decrypt the response
          const decryptedResponse = decryptPayload(response.data.body, {
            key,
            iv,
          });

          const parsed = JSON.parse(decryptedResponse);
          if (parsed?.statusCode === 417) {
            return reject(rejectGuestStepMismatch(parsed));
          }
          resolve(parsed);
        })
        .catch((error) => {
          const defaultError = {
            statusCode: error?.response?.status || 500,
            message: "Something went wrong",
          };

          if (
            error?.response?.status === 403 ||
            error?.message === "Request failed with status code 403"
          ) {
            // Guest enrollment uses the same client; do not wipe guestToken on 401.
            if (localStorage.getItem(MEMBER_TOKEN_KEY)) {
              return reject({
                statusCode: error?.response?.status || 403,
                message: error?.message || "Forbidden",
              });
            }
          }

          if (
            error?.response?.status === 401 ||
            error?.message === "Network Error"
          ) {
            // Guest enrollment uses the same client; do not wipe guestToken on 401.
            // if (localStorage.getItem(MEMBER_TOKEN_KEY)) {
            localStorage.clear();
            window.location.replace("/");
            // }
            return reject({
              statusCode: error?.response?.status || 401,
              message: error?.message || "Unauthorized or Network Error",
            });
          } else if (error?.response?.status === 400) {
            let apiMessage = "Bad Request";
            try {
              const parsed = JSON.parse(
                decryptPayload(error?.response?.data?.body, { key, iv }),
              );
              apiMessage =
                parsed?.message ?? parsed?.data?.message ?? apiMessage;
            } catch {
              /* keep default */
            }
            localStorage.removeItem(MEMBER_TOKEN_KEY);
            localStorage.removeItem(GUEST_TOKEN_KEY);
            localStorage.removeItem(GUEST_LOGIN_RESPONSE_KEY);
            message.error(apiMessage);
            queueMicrotask(goHomeSpa);
            return reject({
              statusCode: 400,
              message: apiMessage,
            });
          }
          try {
            const decryptedResponse = decryptPayload(
              error?.response?.data?.body,
              { key, iv },
            );
            const parsedError = JSON.parse(decryptedResponse);
            const httpStatus = error?.response?.status;
            if (parsedError?.statusCode === 417 || httpStatus === 417) {
              return reject(rejectGuestStepMismatch(parsedError));
            }
            reject({
              statusCode: httpStatus || 500,
              message: parsedError.message || defaultError.message,
            });
          } catch (err) {
            reject(defaultError); // fallback for cases where decryption fails
          }
        });
    });
  }
}
